/**
 * VENDORED from scio3 `apps/web/src/features/audit-logs/constants.ts` (no shared libs with scio3 —
 * copy, drift is fine). Keep the CODES in step with scio1 `imports/api/logs/constants.js`, which is
 * what open-api writes; the labels are scio3's, so staff and the customer read the same words.
 *
 * Audit log entity types.
 */
export const TYPE_LOGS = {
  ASSET: 1,
  PLAYLIST: 2,
  DEVICE: 3,
  SCHEDULE: 4,
  FOLDER: 5,
  LOGIN: 6,
  PROFILE: 7,
  TEAM: 8,
  PREFERENCE: 9,
  APPROVAL: 10,
  USER: 11,
  ROLE: 12,
} as const;

/**
 * Audit log action types — matches scio1 `imports/api/logs/constants.js`.
 */
export const TYPE_LOGS_ACTION = {
  ADD: 1,
  EDIT: 2,
  DELETE: 3,
  MOVE: 4,
  SUBMIT: 5,
  APPROVAL: 6,
  REJECT: 7,
  SERCURITY: 8,
  CHANGE_PASSWORD: 9,
  LOGIN: 10,
  LOGIN_GOOGLE: 11,
  LOGIN_FACEBOOK: 12,
  LOGIN_AZURE: 13,
  MOVE_TEAM: 14,
  MOVE_FOLDER_ASSET: 15,
  MOVE_FOLDER_DEVICE: 16,
  MOVE_FOLDER_PLAYLIST: 17,
  MOVE_FOLDER_SCHEDULE: 18,
  FOLDER_ADD: 19,
  FOLDER_REMOVE: 20,
  FOLDER_RENAME: 21,
  FOLDER_CHANGE_PERMISSION: 22,
  MOVE_ASSET_TO_FOLDER: 23,
  MOVE_DEVICE_TO_FOLDER: 24,
  MOVE_PLAYLIST_TO_FOLDER: 25,
  MOVE_SCHEDULE_TO_FOLDER: 26,
  EDIT_ASSET_TAGS: 27,
  EDIT_ASSET_FOLDER_TAGS: 28,
  EDIT_SCHEDULE_DEFAULT_GLOBAL: 29,
  EDIT_SCHEDULE_DEFAULT: 30,
  EDIT_PROFILE: 31,
  EDIT_CHANGE_PASSWORD: 32,
  ADD_USER: 33,
  CHANGE_ROLE: 34,
  USER_INVITE: 35,
  USER_RESET_PASSWORD: 36,
  ADD_USER_TO_TEAM: 37,
  REMOVE_USER_FROM_TEAM: 38,
  DELETE_USER_FROM_TEAM: 39,
  APPROVAL_APPROVED: 40,
  APPROVAL_REJECT: 41,
  APPROVAL_SUBMIT: 42,
  LOGIN_RESUME: 43,
} as const;

// ---------------------------------------------------------------------------
// Display labels (improved scio3 labels — past tense, fixed phrasing)
// ---------------------------------------------------------------------------

/** Maps TYPE_LOGS values to human-readable entity labels */
export const ENTITY_TYPE_LABELS: Record<number, string> = {
  [TYPE_LOGS.ASSET]: 'Assets',
  [TYPE_LOGS.PLAYLIST]: 'Playlists',
  [TYPE_LOGS.DEVICE]: 'Screens',
  [TYPE_LOGS.SCHEDULE]: 'Schedules',
  [TYPE_LOGS.FOLDER]: 'Folders',
  [TYPE_LOGS.LOGIN]: 'Login',
  [TYPE_LOGS.PROFILE]: 'Profile',
  [TYPE_LOGS.TEAM]: 'Teams',
  [TYPE_LOGS.PREFERENCE]: 'Preferences',
  [TYPE_LOGS.APPROVAL]: 'Approvals',
  [TYPE_LOGS.USER]: 'Users',
  [TYPE_LOGS.ROLE]: 'Roles',
};

/** Maps TYPE_LOGS_ACTION values to display labels */
export const ACTION_TYPE_LABELS: Record<number, string> = {
  [TYPE_LOGS_ACTION.ADD]: 'Added',
  [TYPE_LOGS_ACTION.EDIT]: 'Edited',
  [TYPE_LOGS_ACTION.DELETE]: 'Removed',
  [TYPE_LOGS_ACTION.MOVE]: 'Moved',
  [TYPE_LOGS_ACTION.SUBMIT]: 'Submitted',
  [TYPE_LOGS_ACTION.APPROVAL]: 'Approved',
  [TYPE_LOGS_ACTION.REJECT]: 'Rejected',
  [TYPE_LOGS_ACTION.SERCURITY]: 'Security',
  [TYPE_LOGS_ACTION.CHANGE_PASSWORD]: 'Password Changed',
  [TYPE_LOGS_ACTION.LOGIN]: 'Password Login',
  [TYPE_LOGS_ACTION.LOGIN_GOOGLE]: 'Google Login',
  [TYPE_LOGS_ACTION.LOGIN_FACEBOOK]: 'Facebook Login',
  [TYPE_LOGS_ACTION.LOGIN_AZURE]: 'Microsoft Login',
  [TYPE_LOGS_ACTION.MOVE_TEAM]: 'Moved to Team',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_ASSET]: 'Moved Folder',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_DEVICE]: 'Moved Folder',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_PLAYLIST]: 'Moved Folder',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_SCHEDULE]: 'Moved Folder',
  [TYPE_LOGS_ACTION.FOLDER_ADD]: 'Folder Added',
  [TYPE_LOGS_ACTION.FOLDER_REMOVE]: 'Folder Removed',
  [TYPE_LOGS_ACTION.FOLDER_RENAME]: 'Folder Renamed',
  [TYPE_LOGS_ACTION.FOLDER_CHANGE_PERMISSION]: 'Folder Permissions Changed',
  [TYPE_LOGS_ACTION.MOVE_ASSET_TO_FOLDER]: 'Moved to Folder',
  [TYPE_LOGS_ACTION.MOVE_DEVICE_TO_FOLDER]: 'Moved to Folder',
  [TYPE_LOGS_ACTION.MOVE_PLAYLIST_TO_FOLDER]: 'Moved to Folder',
  [TYPE_LOGS_ACTION.MOVE_SCHEDULE_TO_FOLDER]: 'Moved to Folder',
  [TYPE_LOGS_ACTION.EDIT_ASSET_TAGS]: 'Tags Updated',
  [TYPE_LOGS_ACTION.EDIT_ASSET_FOLDER_TAGS]: 'Folder Tags Updated',
  [TYPE_LOGS_ACTION.EDIT_SCHEDULE_DEFAULT_GLOBAL]: 'Default Schedule Edited',
  [TYPE_LOGS_ACTION.EDIT_SCHEDULE_DEFAULT]: 'Default Schedule Edited',
  [TYPE_LOGS_ACTION.EDIT_PROFILE]: 'Profile Edited',
  [TYPE_LOGS_ACTION.EDIT_CHANGE_PASSWORD]: 'Password Changed',
  [TYPE_LOGS_ACTION.ADD_USER]: 'User Added',
  [TYPE_LOGS_ACTION.CHANGE_ROLE]: 'User Role Changed',
  [TYPE_LOGS_ACTION.USER_INVITE]: 'User Invited to Team',
  [TYPE_LOGS_ACTION.USER_RESET_PASSWORD]: 'User Password Reset',
  [TYPE_LOGS_ACTION.ADD_USER_TO_TEAM]: 'User Added to Team',
  [TYPE_LOGS_ACTION.REMOVE_USER_FROM_TEAM]: 'User Removed from Team',
  [TYPE_LOGS_ACTION.DELETE_USER_FROM_TEAM]: 'User Deleted',
  [TYPE_LOGS_ACTION.APPROVAL_APPROVED]: 'Playlist Approved',
  [TYPE_LOGS_ACTION.APPROVAL_REJECT]: 'Playlist Rejected',
  [TYPE_LOGS_ACTION.APPROVAL_SUBMIT]: 'Playlist Submitted',
  [TYPE_LOGS_ACTION.LOGIN_RESUME]: 'Resume Login',
};

// ---------------------------------------------------------------------------
// Action badge categories and colors
// ---------------------------------------------------------------------------

export const ACTION_BADGE_CATEGORY = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  MOVE: 'MOVE',
  LOGIN: 'LOGIN',
  PERMISSION: 'PERMISSION',
} as const;

export type ActionBadgeCategory =
  (typeof ACTION_BADGE_CATEGORY)[keyof typeof ACTION_BADGE_CATEGORY];

/** Maps action IDs → badge category for coloring */
export const ACTION_CATEGORY_MAP: Record<number, ActionBadgeCategory> = {
  [TYPE_LOGS_ACTION.ADD]: 'CREATE',
  [TYPE_LOGS_ACTION.FOLDER_ADD]: 'CREATE',
  [TYPE_LOGS_ACTION.ADD_USER]: 'CREATE',
  [TYPE_LOGS_ACTION.ADD_USER_TO_TEAM]: 'CREATE',
  [TYPE_LOGS_ACTION.USER_INVITE]: 'CREATE',
  [TYPE_LOGS_ACTION.EDIT]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_ASSET_TAGS]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_ASSET_FOLDER_TAGS]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_SCHEDULE_DEFAULT_GLOBAL]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_SCHEDULE_DEFAULT]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_PROFILE]: 'EDIT',
  [TYPE_LOGS_ACTION.EDIT_CHANGE_PASSWORD]: 'EDIT',
  [TYPE_LOGS_ACTION.FOLDER_RENAME]: 'EDIT',
  [TYPE_LOGS_ACTION.CHANGE_ROLE]: 'EDIT',
  [TYPE_LOGS_ACTION.USER_RESET_PASSWORD]: 'EDIT',
  [TYPE_LOGS_ACTION.APPROVAL_APPROVED]: 'EDIT',
  [TYPE_LOGS_ACTION.APPROVAL_REJECT]: 'EDIT',
  [TYPE_LOGS_ACTION.APPROVAL_SUBMIT]: 'EDIT',
  [TYPE_LOGS_ACTION.DELETE]: 'DELETE',
  [TYPE_LOGS_ACTION.FOLDER_REMOVE]: 'DELETE',
  [TYPE_LOGS_ACTION.REMOVE_USER_FROM_TEAM]: 'DELETE',
  [TYPE_LOGS_ACTION.DELETE_USER_FROM_TEAM]: 'DELETE',
  [TYPE_LOGS_ACTION.MOVE]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_TEAM]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_ASSET]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_DEVICE]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_PLAYLIST]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_FOLDER_SCHEDULE]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_ASSET_TO_FOLDER]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_DEVICE_TO_FOLDER]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_PLAYLIST_TO_FOLDER]: 'MOVE',
  [TYPE_LOGS_ACTION.MOVE_SCHEDULE_TO_FOLDER]: 'MOVE',
  [TYPE_LOGS_ACTION.LOGIN]: 'LOGIN',
  [TYPE_LOGS_ACTION.LOGIN_GOOGLE]: 'LOGIN',
  [TYPE_LOGS_ACTION.LOGIN_FACEBOOK]: 'LOGIN',
  [TYPE_LOGS_ACTION.LOGIN_AZURE]: 'LOGIN',
  [TYPE_LOGS_ACTION.LOGIN_RESUME]: 'LOGIN',
  [TYPE_LOGS_ACTION.FOLDER_CHANGE_PERMISSION]: 'PERMISSION',
  [TYPE_LOGS_ACTION.SERCURITY]: 'PERMISSION',
  [TYPE_LOGS_ACTION.CHANGE_PASSWORD]: 'PERMISSION',
};

/** Badge color classes by category — using semantic-adjacent tokens per DESIGN_SYSTEM.md */
export const BADGE_COLORS: Record<ActionBadgeCategory, { bg: string; text: string }> = {
  CREATE: { bg: 'bg-success/10', text: 'text-success' },
  EDIT: { bg: 'bg-primary/10', text: 'text-primary' },
  DELETE: { bg: 'bg-destructive/10', text: 'text-destructive' },
  MOVE: { bg: 'bg-warning/10', text: 'text-warning' },
  LOGIN: { bg: 'bg-muted', text: 'text-muted-foreground' },
  PERMISSION: { bg: 'bg-accent', text: 'text-accent-foreground' },
};

/** One Action filter option: a label, and every action code that renders with it. */
export interface ActionFilterOption {
  label: string;
  codes: number[];
}

/**
 * The Action filter's options, deduplicated by label and sorted. Several codes share one label
 * ("Moved Folder" is four of them), so choosing a label must send all of its codes.
 */
export const ACTION_FILTER_OPTIONS: ActionFilterOption[] = Object.entries(ACTION_TYPE_LABELS)
  .reduce<ActionFilterOption[]>((acc, [code, label]) => {
    const hit = acc.find((o) => o.label === label);
    if (hit) hit.codes.push(Number(code));
    else acc.push({ label, codes: [Number(code)] });
    return acc;
  }, [])
  .sort((a, b) => a.label.localeCompare(b.label));
